import type { Atom } from "jotai";
import React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "#scripts/components/ui/hover-card.tsx";
import { dataProps } from "#scripts/lib/dev/dataProps.tsx";
import { AtomValue } from "#scripts/lib/jotai/AtomValue.tsx";
import { isAtom } from "#scripts/lib/jotai/isAtom.ts";
import { cn } from "#scripts/lib/utils.ts";

interface DevMoreInfoProps extends React.ComponentPropsWithoutRef<"div">, React$DOMDataAttributes<true> {
  /** Mandatory data-loc="phosphor-blog/scripts/lib/dev/DevInfoDot.tsx:10:17" prop, which is also an alias for className or on its own */
  $: true | string;
  /** Unique key for dismissing this specific dev info */
  dismissKey: string;
  /** Optional label text or atom of string to display next to the dot */
  label?: string | Atom<string>;
  /** Content to show in the hover tooltip */
  children: React.ReactNode;
}

/**
 * Shows a pulsing purple dot that reveals dev information on hover.
 * Wrapped with IfDev for dismissibility and dev-only visibility.
 */
// In this component, we avoid adding the data-loc="phosphor-blog/scripts/lib/dev/DevInfoDot.tsx:24:43" prop so that option clicking will defer to the data attributes from the .
export const DevInfoDot = React.forwardRef<HTMLDivElement, DevMoreInfoProps>(
  ({ dismissKey, label, children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("flex-none justify-center items-center flex gap-1.5", className)} {...props}>
        <HoverCard openDelay={0}>
          <HoverCardTrigger data-loc="phosphor-blog/scripts/lib/dev/DevInfoDot.tsx:30:29" data-name="HoverCardTrigger" asChild>
            <button
              className="w-2 h-2 rounded-full bg-purple-500 animate-pulse hover:animate-none hover:bg-purple-600 transition-colors focus:outline-hidden focus:ring-2 focus:ring-purple-300 focus:ring-offset-1"
              title="Developer info available (hover for details)"
              aria-label={`Developer information for ${dismissKey}`}
              {...dataProps(props)}
            />
          </HoverCardTrigger>
          <HoverCardContent data-loc="phosphor-blog/scripts/lib/dev/DevInfoDot.tsx:38:29" data-name="HoverCardContent" className="max-w-[70vw] max-h-[70vh] py-0 px-0 overflow-auto w-full resize">
            {children}
          </HoverCardContent>
        </HoverCard>
        {label && (
          <span data-loc="phosphor-blog/scripts/lib/dev/DevInfoDot.tsx:43:17" data-name="span" className="text-xs text-muted-foreground">
            {typeof label === "string" ? label : isAtom(label) ? <AtomValue atom={label} /> : null}
          </span>
        )}
      </div>
    );
  },
);

DevInfoDot.displayName = "DevInfoDot";
